package features.attendance.data

import core.firebase.FirebaseService
import features.attendance.domain.AttendanceRecord
import kotlin.js.Promise
import kotlin.js.json
import kotlinx.browser.window

/**
 * Repository for managing attendance records in Firestore
 */
object AttendanceRepository {
    
    /**
     * Clock in a user for today
     * @param teamId Team ID
     * @param userId User ID
     * @param checkInTime Time in HH:mm format
     * @param location Location string
     * @param latitude GPS latitude
     * @param longitude GPS longitude
     * @param note Optional note
     */
    fun clockIn(
        teamId: String,
        userId: String,
        checkInTime: String,
        location: String,
        latitude: Double?,
        longitude: Double?,
        photo: String? = null,
        note: String = ""
    ): Promise<String> {
        return Promise { resolve, reject ->
            try {
                val today = getCurrentDate()
                
                val attendanceData = json(
                    "userId" to userId,
                    "date" to today,
                    "checkIn" to checkInTime,
                    "checkOut" to "-",
                    "status" to "Present",
                    "location" to location,
                    "latitude" to latitude,
                    "longitude" to longitude,
                    "photo" to photo,
                    "note" to note,
                    "createdAt" to js("firebase.firestore.FieldValue.serverTimestamp()")
                )
                
                FirebaseService.db
                    .collection("teams")
                    .doc(teamId)
                    .collection("attendance")
                    .add(attendanceData)
                    .then { docRef ->
                        console.log("✅ Clock in successful, ID:", docRef.id)
                        resolve(docRef.id)
                    }
                    .catch { error ->
                        console.error("❌ Clock in failed:", error)
                        reject(error)
                    }
            } catch (e: Throwable) {
                console.error("❌ Clock in error:", e)
                reject(e)
            }
        }
    }
    
    /**
     * Clock out a user for today
     * @param teamId Team ID
     * @param recordId Attendance record ID
     * @param checkOutTime Time in HH:mm format
     * @param latitude GPS latitude
     * @param longitude GPS longitude
     * @param photo Optional checkout photo
     */
    fun clockOut(
        teamId: String,
        recordId: String,
        checkOutTime: String,
        latitude: Double?,
        longitude: Double?,
        photo: String? = null
    ): Promise<Unit> {
        return Promise { resolve, reject ->
            try {
                val updateData = json(
                    "checkOut" to checkOutTime,
                    "checkOutLatitude" to latitude,
                    "checkOutLongitude" to longitude,
                    "checkOutPhoto" to photo,
                    "updatedAt" to js("firebase.firestore.FieldValue.serverTimestamp()")
                )
                
                FirebaseService.db
                    .collection("teams")
                    .doc(teamId)
                    .collection("attendance")
                    .doc(recordId)
                    .update(updateData)
                    .then {
                        console.log("✅ Clock out successful")
                        resolve(Unit)
                    }
                    .catch { error ->
                        console.error("❌ Clock out failed:", error)
                        reject(error)
                    }
            } catch (e: Throwable) {
                console.error("❌ Clock out error:", e)
                reject(e)
            }
        }
    }
    
    /**
     * Get today's attendance record for a user
     */
    fun getTodayAttendance(teamId: String, userId: String): Promise<AttendanceRecord?> {
        return Promise { resolve, reject ->
            try {
                val today = getCurrentDate()
                console.log("🔍 log-attendance: getTodayAttendance() called")
                console.log("🔍 log-attendance: Team ID:", teamId)
                console.log("🔍 log-attendance: User ID:", userId)
                console.log("🔍 log-attendance: Today date:", today)
                
                FirebaseService.db
                    .collection("teams")
                    .doc(teamId)
                    .collection("attendance")
                    .where("userId", "==", userId)
                    .where("date", "==", today)
                    .get()
                    .then { snapshot: dynamic ->
                        console.log("🔍 log-attendance: Query returned, snapshot:", snapshot)
                        console.log("🔍 log-attendance: Snapshot empty?:", snapshot.empty)
                        
                        if (!snapshot.empty) {
                            // Access first document using JavaScript
                            val docsArray: dynamic = snapshot.docs
                            val firstDoc: dynamic = docsArray[0]
                            
                            console.log("🔍 log-attendance: First doc:", firstDoc)
                            console.log("🔍 log-attendance: Doc ID:", firstDoc.id)
                            
                            // Call data() using JavaScript function call
                            val dataFunction: dynamic = firstDoc.data
                            val rawData: dynamic = js("dataFunction.call(firstDoc)")
                            
                            console.log("🔍 log-attendance: Raw data:", rawData)
                            
                            if (rawData != null && rawData != js("undefined")) {
                                // Access fields using JavaScript property access
                                val userId: String = js("rawData.userId || ''").toString()
                                val date: String = js("rawData.date || ''").toString()
                                val checkIn: String = js("rawData.checkIn || '-'").toString()
                                val checkOut: String = js("rawData.checkOut || '-'").toString()
                                val status: String = js("rawData.status || 'Absent'").toString()
                                val location: String = js("rawData.location || ''").toString()
                                val checkInPhoto: String? = js("rawData.photo || null") as? String
                                val checkOutPhoto: String? = js("rawData.checkOutPhoto || null") as? String
                                
                                console.log("🔍 log-attendance: Parsed fields:")
                                console.log("   userId:", userId)
                                console.log("   date:", date)
                                console.log("   checkIn:", checkIn)
                                console.log("   checkOut:", checkOut)
                                console.log("   status:", status)
                                
                                val record = AttendanceRecord(
                                    id = firstDoc.id.toString().hashCode(),
                                    userId = userId,
                                    date = date,
                                    checkIn = checkIn,
                                    checkOut = checkOut,
                                    status = status,
                                    location = location,
                                    checkInPhoto = checkInPhoto,
                                    checkOutPhoto = checkOutPhoto
                                )
                                
                                console.log("✅ log-attendance: Record created successfully")
                                console.log("   checkIn: ${record.checkIn}, checkOut: ${record.checkOut}")
                                resolve(record)
                            } else {
                                console.warn("⚠️ log-attendance: rawData is null or undefined")
                                resolve(null)
                            }
                        } else {
                            console.warn("⚠️ log-attendance: No documents found for today")
                            resolve(null)
                        }
                    }
                    .catch { error: dynamic ->
                        console.error("❌ log-attendance: Get today attendance failed:", error)
                        reject(error)
                    }
            } catch (e: Throwable) {
                console.error("❌ log-attendance: Get today attendance error:", e)
                reject(e)
            }
        }
    }
    
    /**
     * Get all attendance records for a team
     */
    fun getAttendanceRecords(teamId: String): Promise<List<AttendanceRecord>> {
        return Promise { resolve, reject ->
            try {
                FirebaseService.db
                    .collection("teams")
                    .doc(teamId)
                    .collection("attendance")
                    .get()
                    .then { snapshot ->
                        val records = mutableListOf<AttendanceRecord>()
                        snapshot.forEach { doc ->
                            val data = doc.data() as? Map<String, Any>
                            if (data != null) {
                                val record = AttendanceRecord(
                                    id = doc.id.toIntOrNull() ?: 0,
                                    userId = data["userId"] as? String ?: "",
                                    date = data["date"] as? String ?: "",
                                    checkIn = data["checkIn"] as? String ?: "-",
                                    checkOut = data["checkOut"] as? String ?: "-",
                                    status = data["status"] as? String ?: "Absent",
                                    location = data["location"] as? String ?: "-"
                                )
                                records.add(record)
                            }
                        }
                        console.log("✅ Loaded ${records.size} attendance records")
                        resolve(records)
                    }
                    .catch { error ->
                        console.error("❌ Get attendance records failed:", error)
                        reject(error)
                    }
            } catch (e: Throwable) {
                console.error("❌ Get attendance records error:", e)
                reject(e)
            }
        }
    }
    
    /**
     * Get attendance records filtered by user role
     * Admin/Owner can see all records, members can only see their own
     * @param teamId Team ID
     * @param userId Current user ID
     * @param userRole User's role in the team (owner, admin, or member)
     */
    fun getAttendanceRecordsForUser(
        teamId: String,
        userId: String,
        userRole: String
    ): Promise<List<AttendanceRecord>> {
        return Promise { resolve, reject ->
            try {
                console.log("🔍 log-attendance: getAttendanceRecordsForUser() called")
                console.log("🔍 log-attendance: Team ID: $teamId, User ID: $userId, Role: $userRole")
                
                val baseQuery = FirebaseService.db
                    .collection("teams")
                    .doc(teamId)
                    .collection("attendance")
                
                val query: dynamic = if (userRole == "owner" || userRole == "admin") {
                    // Admin/Owner can see all attendance records
                    console.log("🔍 log-attendance: Loading ALL records (admin/owner)")
                    baseQuery
                } else {
                    // Regular members can only see their own records
                    console.log("🔍 log-attendance: Loading filtered records (member) for userId: $userId")
                    baseQuery.where("userId", "==", userId)
                }
                
                query.get()
                    .then { snapshot: dynamic ->
                        console.log("🔍 log-attendance: Query returned ${snapshot.size} documents")
                        val records = mutableListOf<AttendanceRecord>()
                        snapshot.forEach { doc: dynamic ->
                            try {
                                console.log("🔍 log-attendance: ========== Processing document:", doc.id, "==========")
                                
                                // Call doc.data() to get raw Firebase data
                                val rawData: dynamic = doc.data()
                                console.log("✅ log-attendance: doc.data() called successfully")
                                console.log("🔍 log-attendance: Raw data:", rawData)
                                
                                // FIXED: Access fields directly from rawData instead of converting to Map
                                val userId = rawData.userId as? String ?: ""
                                val date = rawData.date as? String ?: ""
                                val checkIn = rawData.checkIn as? String ?: "-"
                                val checkOut = rawData.checkOut as? String ?: "-"
                                val status = rawData.status as? String ?: "Absent"
                                val location = rawData.location as? String ?: "-"
                                val checkInPhoto = rawData.photo as? String
                                val checkOutPhoto = rawData.checkOutPhoto as? String
                                
                                console.log("🔍 log-attendance: Extracted fields:")
                                console.log("  - userId:", userId)
                                console.log("  - date:", date)
                                console.log("  - checkIn:", checkIn)
                                console.log("  - checkOut:", checkOut)
                                console.log("  - status:", status)
                                
                                val record = AttendanceRecord(
                                    id = doc.id.toString().hashCode(),
                                    userId = userId,
                                    date = date,
                                    checkIn = checkIn,
                                    checkOut = checkOut,
                                    status = status,
                                    location = location,
                                    checkInPhoto = checkInPhoto,
                                    checkOutPhoto = checkOutPhoto
                                )
                                records.add(record)
                                console.log("✅ log-attendance: Added record: $userId | $date | $checkIn")
                            } catch (e: Throwable) {
                                console.error("❌ log-attendance: Error processing doc:", e)
                                console.error("❌ log-attendance: Error details:", e.message)
                            }
                        }
                        console.log("✅ log-attendance: Loaded ${records.size} attendance records for user role: $userRole")
                        resolve(records)
                    }
                    .catch { error: dynamic ->
                        console.error("❌ Get filtered attendance records failed:", error)
                        reject(error)
                    }
            } catch (e: Throwable) {
                console.error("❌ Get filtered attendance records error:", e)
                reject(e)
            }
        }
    }
    
    /**
     * Get current date in YYYY-MM-DD format
     */
    private fun getCurrentDate(): String {
        val now = js("new Date()")
        val year = now.getFullYear()
        val month = (now.getMonth() + 1).toString().padStart(2, '0')
        val day = now.getDate().toString().padStart(2, '0')
        return "$year-$month-$day"
    }
    
    /**
     * Get current time in HH:mm format
     */
    fun getCurrentTime(): String {
        val now = js("new Date()")
        val hours = now.getHours().toString().padStart(2, '0')
        val minutes = now.getMinutes().toString().padStart(2, '0')
        return "$hours:$minutes"
    }
}
